// The visitor's trail through the site (kept in sessionStorage by the client) and the Back card's choice of
// destination. Pure, so tests/navTrail.test.js runs it under plain node.
import {safeBackPath} from "./returnLink.js";

export const TRAIL_KEY = "naples:trail";
export const TRAIL_MAX = 30;

const str = (v, max = 400) => (typeof v === "string" ? v.slice(0, max) : "");

// One page view: {path, title, description, image, imageAlt, group, from, at}.
export function makeEntry({path, title, description, image, imageAlt, group, back, at} = {}) {
    return {
        path: str(path, 300),
        title: str(title, 200),
        description: str(description, 320),
        image: str(image, 600),
        imageAlt: str(imageAlt, 220),
        group: typeof group === "string" && group ? group : null,
        from: safeBackPath(back),
        at: Number.isFinite(at) ? at : Date.now(),
    };
}

// Adds a view. The same path again (a refresh) updates the last entry instead of repeating it.
// `pop` is "back" or "forward" for a browser step: a step back drops everything after the page's last visit.
export function pushView(trail, entry, pop = "") {
    const list = Array.isArray(trail) ? trail.slice() : [];
    if (!entry || !entry.path) return list;
    if (pop === "back") {
        const at = list.map((e) => e.path).lastIndexOf(entry.path);
        if (at >= 0) {
            list.length = at + 1;
            list[at] = {...list[at], ...fresh(entry)};
            return list;
        }
    }
    const last = list[list.length - 1];
    if (last && last.path === entry.path) {
        list[list.length - 1] = {...last, ...fresh(entry), from: entry.from ?? last.from};
    } else {
        list.push(entry);
    }
    return list.length > TRAIL_MAX ? list.slice(list.length - TRAIL_MAX) : list;
}

// Only what the new view really knows overwrites what the entry had.
function fresh(entry) {
    const out = {};
    for (const k of ["title", "description", "image", "imageAlt", "group"]) if (entry[k]) out[k] = entry[k];
    return out;
}

// Fills the metadata of the last entry for `path` (page data arrives after the route changes).
export function annotate(trail, path, meta) {
    const list = Array.isArray(trail) ? trail.slice() : [];
    for (let i = list.length - 1; i >= 0; i--) {
        if (list[i].path === path) {
            list[i] = {...list[i], ...fresh(meta)};
            break;
        }
    }
    return list;
}

// Stored form: {host, entries}. Another host's trail (or junk) reads as empty.
export function parseTrail(raw, host) {
    try {
        const v = typeof raw === "string" ? JSON.parse(raw) : raw;
        if (!v || v.host !== host || !Array.isArray(v.entries)) return [];
        return v.entries
            .filter((e) => e && typeof e.path === "string" && safeBackPath(e.path) === e.path)
            .map((e) => makeEntry({...e, back: e.from, at: e.at}))
            .slice(-TRAIL_MAX);
    } catch {
        return [];
    }
}

export const stringifyTrail = (entries, host) => JSON.stringify({host, entries});

// The page's Explore group landing link: the group whose pages include `path`, or whose own link it is.
export function groupLinkOf(navData, path) {
    for (const g of Array.isArray(navData) ? navData : []) {
        if (!g || typeof g.link !== "string") continue;
        if (g.link === path || (Array.isArray(g.links) && g.links.some((l) => l && l.link === path))) return g.link;
    }
    return null;
}

// excludePaths may hold strings or the studio's {link} objects.
export function excludeList(list) {
    return (Array.isArray(list) ? list : [])
        .map((x) => (typeof x === "string" ? x : x && typeof x === "object" ? x.link : ""))
        .filter((x) => typeof x === "string" && x.trim() !== "")
        .map((x) => x.trim().replace(/\/+$/, "") || "/");
}

// Prefix match on whole path segments: "/services" excludes "/services" and "/services/x", not "/servicesx".
export function isExcluded(path, excludes) {
    return excludeList(excludes).some((x) => (x === "/" ? path === "/" : path === x || path.startsWith(`${x}/`)));
}

// The destination for a strategy: {path, entry} (entry when the trail knows the page) or null.
// current: {path, from, group}; trail includes the current view as its last entry.
export function resolveBack({strategy = "origin", trail = [], current, queryBack = null, excludes = [], groupLink = null}) {
    if (!current || !current.path) return null;
    const entries = Array.isArray(trail) ? trail : [];
    const ok = (p) => typeof p === "string" && p !== "" && p !== current.path && !isExcluded(p, excludes);
    const pick = (p) => (ok(p) ? {path: p, entry: lastEntry(entries, p)} : null);

    const previous = () => {
        for (let i = entries.length - 1; i >= 0; i--) {
            if (entries[i].path !== current.path && ok(entries[i].path)) return {path: entries[i].path, entry: entries[i]};
        }
        return null;
    };

    if (strategy === "previous") return previous();
    if (strategy === "section") return pick(groupLink);

    // origin: follow the "from" links back; the farthest usable page wins.
    const chain = [];
    const seen = new Set([current.path]);
    let next = safeBackPath(current.from) || safeBackPath(queryBack);
    let upTo = entries.length - 1;
    while (next && !seen.has(next) && chain.length < TRAIL_MAX) {
        seen.add(next);
        chain.push(next);
        // The latest visit of that page before the one we came from.
        let at = -1;
        for (let i = Math.min(upTo, entries.length - 1); i >= 0; i--) if (entries[i].path === next) { at = i; break; }
        if (at < 0) break;
        upTo = at - 1;
        next = safeBackPath(entries[at].from);
    }
    for (let i = chain.length - 1; i >= 0; i--) {
        const hit = pick(chain[i]);
        if (hit) return hit;
    }
    return previous();
}

function lastEntry(entries, path) {
    for (let i = entries.length - 1; i >= 0; i--) if (entries[i].path === path) return entries[i];
    return null;
}

// The page's name for "Back to <name>": its share title without the site name on the end ("Cushions · Marmalade
// Moon" -> "Cushions"). A page whose title is only the site name is named from its address (/shop -> "Shop"); home
// keeps the site name.
export function cardTitle(title, siteName, path) {
    const t = String(title ?? "").trim();
    const site = String(siteName ?? "").trim();
    if (site && t.length > site.length) {
        for (const sep of [" · ", " | ", " - "]) {
            if (t.endsWith(sep + site)) return t.slice(0, -(sep + site).length).trim();
        }
    }
    if (site && t === site && path && path !== "/") {
        const last = path.replace(/\/+$/, "").split("/").pop() || "";
        const named = decodeURIComponent(last).replace(/[-_]+/g, " ").trim();
        if (named) return named.charAt(0).toUpperCase() + named.slice(1);
    }
    return t;
}
